import { useState } from 'react';
import { Alert, Button, Typography } from 'antd';

import EventFormElement from '../events/EventFormElement';
import ItemLookupSelect from '../common/ItemLookupSelect';
import { previewAutomation } from '../../services/automations';
import { getElementInitialValue, isElementValueEmpty } from '../../utils/elements';
import { inputAsElement } from '../../utils/cardActions';
import { buildInput, previewValueText, refusalTarget } from '../../utils/cardActionEditor';
import { normalizeId } from '../../utils/ids';

const { Text } = Typography;

const elementLabelOf = el => el?.label?.singular || el?.name;

/** A value map as label / value pairs, read aloud. */
function ValueList({ values, elements, pressed }) {
	const defs = new Map((elements || []).map(el => [el?.name, el]));
	const entries = Object.entries(values || {});
	if (!entries.length) return null;
	return (
		<ul className="card-action-preview__values">
			{entries.map(([name, value]) => (
				<li key={name}>
					<span className="card-action-preview__name">{elementLabelOf(defs.get(name)) || name}</span>
					<span className="card-action-preview__value">{previewValueText(value, defs.get(name), pressed)}</span>
				</li>
			))}
		</ul>
	);
}

/**
 * What a press would write, for a record the manager picks and sample values
 * for the action's inputs (KPR-550; keepr-api POST .../automations/preview
 * with `press`, KPR-382). It sends the editor's CURRENT, unsaved action, so a
 * manager sees the result before it exists; the API runs the save's own
 * validation first, so a refusal is the save's, said where it belongs
 * (`onRefusal`). Nothing is written.
 *
 * The API renders every value as the press would write it; this only reads
 * them aloud. Records a person cannot read, and deleted ones, are the API's
 * 404 and say so in its words.
 */
export default function CardActionPreview({ collectionId, cardId, cardName, steps, inputs, buildBody, elementsFor, onRefusal, disabledReason = '' }) {
	const [itemId, setItemId] = useState('');
	const [values, setValues] = useState({});
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState(null);
	const [problem, setProblem] = useState('');

	const sampleInputs = (inputs || []).filter(input => input.key).map(input => ({ draft: input, element: inputAsElement(buildInput(input)) }));

	const run = async () => {
		if (busy) return;
		setBusy(true);
		setProblem('');
		try {
			const given = {};
			sampleInputs.forEach(({ draft, element }) => {
				const value = values[draft.key];
				if (value !== undefined && !isElementValueEmpty(element, value)) given[draft.key] = value;
			});
			const answer = await previewAutomation(collectionId, {
				...buildBody(),
				press: { ...(itemId ? { itemId } : {}), inputs: given },
			});
			setResult(answer);
		} catch (error) {
			setResult(null);
			const data = error?.response?.data;
			if (data && error.response.status === 400) {
				const target = refusalTarget(data);
				onRefusal?.(target);
				setProblem(target.message);
			} else {
				setProblem(data?.message || 'The preview could not be made. Nothing was changed.');
			}
		}
		setBusy(false);
	};

	const pressedItem = result?.press?.item || null;
	const pressed = { pressedId: normalizeId(pressedItem?.id), pressedName: cardName };
	const answered = result?.actions || [];
	return (
		<div className="card-action-preview" data-testid="card-action-preview">
			<Text type="secondary" className="d-block">
				Choose a {cardName || 'record'} and, if the button asks for values, try some. Nothing is written.
			</Text>
			<div className="card-action-preview__field">
				<Text className="d-block">Record to press it on</Text>
				{cardId ? (
					<ItemLookupSelect
						collectionId={collectionId}
						cardId={cardId}
						titles={false}
						value={itemId}
						ariaLabel="Record to press it on"
						placeholder="The newest one"
						style={{ width: '100%' }}
						onChange={next => { setItemId(Array.isArray(next) ? (next[0] || '') : (next || '')); setResult(null); }}
					/>
				) : <Text type="secondary" className="d-block">Choose the card first.</Text>}
			</div>
			{sampleInputs.map(({ draft, element }) => (
				<div key={draft.uid} className="item-form-field card-action-preview__field">
					<EventFormElement
						event={{ _id: null, elements: values }}
						element={element}
						value={values[draft.key] === undefined ? getElementInitialValue(element, { isNewRecord: true }) : values[draft.key]}
						onChange={(name, value) => { setValues(prev => ({ ...prev, [name]: value })); setResult(null); }}
						showIconBadge={false}
					/>
				</div>
			))}
			<Button onClick={run} loading={busy} disabled={Boolean(disabledReason) || !cardId}>Preview a press</Button>
			{disabledReason ? <Text type="secondary" className="d-block">{disabledReason}</Text> : null}
			{problem ? <Alert type="error" showIcon role="alert" message={problem} className="card-action-preview__alert" /> : null}
			{result ? (
				<div className="card-action-preview__result" aria-live="polite">
					<Text strong className="d-block">
						{pressedItem ? `Pressed on “${pressedItem.title || 'Untitled'}”` : `There is no ${cardName || 'record'} yet to press it on.`}
					</Text>
					{(result.warnings || []).map((warning, index) => (
						<Alert key={`${warning.code}-${index}`} type="warning" showIcon message={warning.message} className="card-action-preview__alert" />
					))}
					{answered.map(action => {
						const writes = action.writes || {};
						const step = steps[action.index];
						return (
							<div key={action.index} className="card-action-preview__action">
								<Text strong className="d-block">{`${action.index + 1}. ${action.summary || action.type}`.replace(/\*/g, '')}</Text>
								{action.type === 'set-elements' ? (<>
									<ValueList values={writes.elements} elements={elementsFor(cardId)} pressed={pressed} />
									{writes.unchanged?.length ? <Text type="secondary" className="d-block">{`Already holds this: ${writes.unchanged.join(', ')}.`}</Text> : null}
								</>) : null}
								{action.type === 'create-item' ? (<>
									{writes.error ? <Alert type="error" showIcon message={writes.error} /> : (
										<Text className="d-block">
											{writes.duplicate ? 'A matching item already exists, so a press skips this step.' : 'Would create:'}
										</Text>
									)}
									{!writes.error ? <ValueList values={writes.elements} elements={elementsFor(writes.card_id || step?.cardId)} pressed={pressed} /> : null}
								</>) : null}
								{action.type === 'update-items' ? (<>
									<Text className="d-block">{Number.isFinite(action.matches) ? `${action.matches} matching ${action.matches === 1 ? 'item' : 'items'} today.` : 'The matching items could not be counted here.'}</Text>
									<ValueList values={writes.set} elements={[]} pressed={pressed} />
								</>) : null}
							</div>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
